import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { event } from '@uhg-abyss/web/tools/event';
import { CoreFeatures } from '@src/common/CoreFeatures';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { validatePermissions } from '@sde/widgets-core/src/redux/resources/user/helpers';
import { PageLayout } from '@src/common/PageLayout';
import { useSearchResults } from '@src/state/searchResults';
import { styled } from '@uhg-abyss/web/tools/styled';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { usePPCUserInfoQuery } from '@sde/widgets-core/src/common/hooks/usePPCUserInfoQuery';
import { YourFeaturedNewsWidget } from '@sde/widgets-core/src/widgets/YourFeaturedNews';
import { WelcomeContent } from './WelcomeContent/WelcomeContent';
import { ClaimsNotificationWidget } from './ClaimsNotificationWidget';
import { DashboardTooltipModal } from './DashboardTooltipModal';
import { DigitalSecurityError } from './DigitalSecurityError';
import { TermsOfUseModal } from './TermsOfUseModal';
import { FiveTab } from './FiveTab';
import { GoldCardAlert } from './GoldCardEnterpriseBanner/GoldCardAlert';

import { usePayer } from '@src/state/payer';
import { useDashboard } from '@src/state/dashboard';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import { OONConsent } from './OONConsent';
import { AccessRequestsBar } from './AccessRequestsBar';
import { config } from '@uhg-abyss/web/tools/config';

const theme = createTheme('uhc', { enterpriseFont: true });

const RenderTrackItActionRequired = React.lazy(() =>
  import('./TrackItActionRequired').then((m) => ({
    default: m.TrackItActionRequired,
  }))
);

let hasLoaded = false;

const MarginTop = styled.div({
  marginTop: 25,
});

const FadeIn = styled.div({
  justifyContent: 'center',
  opacity: ({ isReady }) => (isReady ? 1 : 0),
  transitionPropery: 'opacity',
  transitionDuration: '800ms',
  transitionDelay: '400ms',
});

const providerOrgKeys = [
  'id',
  'corpMpin',
  'uhcProviderId',
  'firstName',
  'lastName',
  'registeredOrg',
  'paa',
  'ia',
  'uniqueId',
];

export const Landing = ({ digitalSecurityError }) => {
  const { payer } = usePayer.state();
  const { providerPreferenceComplete } = useDashboard.state();
  const [widgetTabsOrder, setWidgetTabsOrder] = useState([]);
  const [isTabPreferencesLoading, setIsTabPreferencesLoading] = useState(true);
  const [canShowTrainingTips, setCanShowTrainingTips] = useState(false);
  const [isProviderPreferenceLoading, setIsProviderPreferenceLoading] =
    useState(true);
  const [isReady, setReady] = useState(hasLoaded);
  const { user } = useUser.state();
  const { code, message, ia, paa } = user;
  const appPermissions = {
    claims: validatePermissions(user, 'claims.read'),
    eligibility: validatePermissions(user, 'eligibility.read'),
    referrals: validatePermissions(user, 'referrals.read'),
    paan: validatePermissions(user, 'prior-auth.read'),
  };
  const breakPoint = 1292;

  const { clearSearchResults } = useSearchResults.actions();
  const isDigitalSecurityAPI = getFeature('DIGITAL_SECURITY_APIS');
  const [, saveOHIDInfo] = usePPCUserInfoQuery({});
  const [isOhidSaveInfoRequested, setOhidSaveInfoRequested] = useState(false);
  const [isEnrollmentRequested, setIsEnrollmentRequested] = useState(false);
  const [actionRequiredCount, setActionRequiredCount] = useState(0);

  useEffect(() => {
    event('LANDING_PAGE_LOAD');
  }, []);

  useEffect(() => {
    if (
      payer.payerId &&
      getFeature('UHCPP_LANDING_PAGE_CHAT') &&
      providerPreferenceComplete
    ) {
      event('UHCPP_LANDING_PAGE_CHAT_LOAD');
    }
  }, [payer.payerId, user.providerOrg, providerPreferenceComplete]);

  useEffect(() => {
    if (
      user?.uuid &&
      !isOhidSaveInfoRequested &&
      getFeature('PPC_OHID_INFO_SAVE_ON_LANDING_PAGE')
    ) {
      setOhidSaveInfoRequested(true);
      saveOHIDInfo({});
    }
  }, [user?.uuid]);

  useEffect(() => {
    if (user.firstName) {
      hasLoaded = true;
      setReady(true);
    }

    let timeout;
    if (user.hasPermissions) {
      timeout = setTimeout(() => {
        hasLoaded = true;
        setReady(true);
      }, 2000);
    }

    return () => {
      clearTimeout(timeout);
      clearSearchResults();
    };
  }, [user.firstName, user.hasPermissions]);

  const isDigitalSecurityError =
    digitalSecurityError && digitalSecurityError.isDigitalSecurityError;

  const WelcomeContentProps = {
    isReady,
    appPermissions,
    digitalSecurityError,
    setWidgetTabsOrder,
    setIsTabPreferencesLoading,
    setIsProviderPreferenceLoading,
  };

  return (
    <ThemeProvider theme={theme}>
      <PageLayout data-testid='landing-content-area'>
        <MarginTop />
        <If condition={!isProviderPreferenceLoading && canShowTrainingTips}>
          <DashboardTooltipModal uuid={user.uuid} />
        </If>
        <TermsOfUseModal setCanShowTrainingTips={setCanShowTrainingTips} />
        <CoreFeatures toggle='OON_CONSENT_MODAL'>
          <OONConsent />
        </CoreFeatures>
        <Choose>
          <When condition={isDigitalSecurityAPI && user.isUserApiError}>
            <DigitalSecurityError
              code={code}
              message={message}
              adminErrCode={user.adminErrCode}
              adminErrMessage={user.adminErrMessage}
              adminInfo={user.adminInfo}
            />
          </When>
          <Otherwise>
            <If
              condition={
                getFeature('TRACKIT_OVERALL') &&
                getFeature('TRACKIT_ACTION_REQUIRED_HOME_PAGE') &&
                !getFeature('TABBED_ACTION_REQUIRED_WIDGET') &&
                !isDigitalSecurityError
              }
            >
              <RenderTrackItActionRequired />
            </If>
            <If
              condition={
                (paa || ia) &&
                !isDigitalSecurityError &&
                !(config('ENV_VAR') == 'local') // removing from local as digisec is not always ran with core
              }
            >
              <AccessRequestsBar />
            </If>

            <WelcomeContent {...WelcomeContentProps} />

            <If
              condition={
                getFeature('CLAIM_ALERTS') &&
                getFeature('CLAIMS_TRACKIT') &&
                getFeature('TRACKIT_OVERALL') &&
                !isDigitalSecurityError &&
                appPermissions.claims
              }
            >
              <ClaimsNotificationWidget />
            </If>
            <If condition={!isDigitalSecurityError}>
              <If condition={getFeature('GC_BANNER')}>
                <GoldCardAlert />
              </If>
              <FadeIn isReady={isReady}>
                <LoadingOverlay
                  loadingTitle='Please Wait'
                  loadingMessage='Retrieving tab preferences.'
                  isOpen={isTabPreferencesLoading}
                  isLoading={isTabPreferencesLoading}
                >
                  <FiveTab
                    widgetTabsOrder={widgetTabsOrder}
                    breakPoint={breakPoint}
                    actionRequiredCount={actionRequiredCount}
                    setActionRequiredCount={setActionRequiredCount}
                  />
                  <div
                    data-testid='fiveTab-tools-wrapper'
                    style={{
                      zIndex: -1,
                      position: 'absolute',
                      height: '700px',
                      width: '26%',
                      left: '0px',
                      top: '0px',
                    }}
                  />
                  <div
                    data-testid='fiveTab-component-wrapper'
                    style={{
                      zIndex: -1,
                      position: 'absolute',
                      height: '700px',
                      width: '75%',
                      right: '0px',
                      top: '0px',
                    }}
                  />
                </LoadingOverlay>
                <If condition={getFeature('SDE_CORE_YOUR_FEATURED_NEWS')}>
                  <YourFeaturedNewsWidget />
                </If>
              </FadeIn>
            </If>
          </Otherwise>
        </Choose>
      </PageLayout>
    </ThemeProvider>
  );
};

Landing.propTypes = {
  digitalSecurityError: PropTypes.shape({
    code: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    isDigitalSecurityError: PropTypes.bool.isRequired,
    isProviderPreferenceError: PropTypes.bool,
  }),
};

Landing.defaultProps = {
  digitalSecurityError: PropTypes.shape({
    isProviderPreferenceError: false,
  }),
};
